import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useQueryClient } from "@tanstack/react-query";
import { FileLibraryPicker } from "@/components/FileLibraryPicker";
import { TemplatePicker, SaveTemplate } from "@/components/MessageTemplates";
import { MessagePreview } from "@/components/MessagePreview";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PatientMedications } from "@/components/PatientMedications";
import { Paperclip, Send, X, FileText, Film, Image as ImageIcon } from "lucide-react";

const ACCEPT = "video/*,image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mov,.heic";
const CHUNK = 4 * 1024 * 1024;
const MAX = 500 * 1024 * 1024;
const fileIcon = (f) => (f.type.startsWith("video/") ? Film : f.type.startsWith("image/") ? ImageIcon : FileText);
const mb = (n) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

async function postChunk(fd, onUp) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return (await api.post("/shared-files/chunk", fd, { timeout: 120000, onUploadProgress: (ev) => onUp?.(ev.loaded || 0) })).data;
    } catch (e) {
      const s = e.response?.status;
      if (s && s < 500 && s !== 408 && s !== 429) throw e;
      if (attempt >= 3) {
        if (!e.response?.data?.detail) e.response = { ...(e.response || {}), data: { detail: s ? `Upload failed (server error ${s}), please try again` : "Upload failed: connection lost, please check your internet and try again" } };
        throw e;
      }
      await new Promise((r) => setTimeout(r, 1500 * attempt));
    }
  }
}

async function waitForFile(uploadId, onProcessing) {
  onProcessing?.();
  const until = Date.now() + 15 * 60 * 1000;
  while (Date.now() < until) {
    await new Promise((r) => setTimeout(r, 2500));
    try {
      const job = (await api.get(`/shared-files/upload/${uploadId}`)).data;
      if (job.status === "done") return job;
      if (job.status === "failed") throw Object.assign(new Error("failed"), { response: { data: { detail: job.error } } });
    } catch (e) {
      if (e.message === "failed") throw e;
    }
  }
  throw Object.assign(new Error("timeout"), { response: { data: { detail: "Saving the file is taking too long, please try again" } } });
}

async function uploadFile(file, onProgress, onProcessing) {
  const uploadId = crypto.randomUUID().replace(/-/g, "");
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const started = Date.now();
  const report = (sent) => {
    const secs = (Date.now() - started) / 1000;
    const speed = secs > 0.5 ? sent / secs : 0;
    onProgress({ pct: Math.min(100, Math.round((sent / file.size) * 100)), sent, size: file.size, speed,
      eta: speed > 0 ? (file.size - sent) / speed : null });
  };
  let res;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("index", i); fd.append("total", total);
    fd.append("filename", file.name); fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
    res = await postChunk(fd, (loaded) => report(Math.min(file.size, i * CHUNK + loaded)));
    report(Math.min(file.size, (i + 1) * CHUNK));
  }
  if (res?.processing) return waitForFile(uploadId, onProcessing);
  if (!res?.file_id) throw Object.assign(new Error("incomplete"), { response: { data: { detail: "The upload didn't finish, please try again" } } });
  return res;
}

const fmtEta = (s) => (s == null ? "working out time left…" : s < 10 ? "a few seconds left" : s < 60 ? `about ${Math.round(s / 5) * 5} sec left` : `about ${Math.ceil(s / 60)} min left`);
const UploadStats = ({ p }) => (
  <div data-testid="patient-message-upload-stats" className="flex flex-wrap justify-between gap-x-3 text-[11px] text-slate-500 mt-1">
    <span data-testid="patient-message-upload-sent">{mb(p.sent)} of {mb(p.size)} ({p.pct}%)</span>
    <span data-testid="patient-message-upload-speed">{p.speed ? `${mb(p.speed)}/s · ` : ""}{fmtEta(p.eta)}</span>
  </div>
);

export const PatientMessageDialog = ({ appt, onClose, endpoint, onSent }) => {
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saveLib, setSaveLib] = useState(false);
  const [libName, setLibName] = useState("");
  const qc = useQueryClient();
  const inputRef = useRef(null);

  const reset = () => { setBody(""); setFile(null); setUploaded(null); setSaved(null); setSaveLib(false); setLibName(""); setProgress(null); setBusy(false); };
  const close = () => { if (!busy) { reset(); onClose(); } };
  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX) { toast.error("File is larger than 500 MB"); return; }
    setFile(f); setUploaded(null); setSaved(null); setLibName(f.name.replace(/\.[^.]+$/, ""));
  };
  const send = async () => {
    setBusy(true);
    try {
      let up = file ? uploaded : null;
      if (file && !up) {
        up = await uploadFile(file, setProgress, () => setStage("saving")); setUploaded(up); setStage(null);
        if (saveLib && libName.trim()) {
          try { await api.post(`/shared-files/${up.file_id}/library`, { name: libName }); qc.invalidateQueries({ queryKey: ["file-library"] }); }
          catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add the file to saved files"); }
        }
      }
      await api.post(endpoint || `/appointments/${appt.id}/send-message`, { body, file_id: saved?.id || up?.file_id || null });
      toast.success(`Text sent to ${appt.patient_name}`);
      onSent?.();
      reset(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      setBusy(false); setStage(null);
    }
  };
  const Icon = file ? fileIcon(file) : null;

  return (
    <Dialog open={!!appt} onOpenChange={(o) => { if (!o) close(); }}>
      {appt && (
        <DialogContent data-testid="patient-message-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Message {appt.patient_name}</DialogTitle>
            <DialogDescription>
              Sent by text to the number on file{appt.patient_phone ? `: ${appt.patient_phone}` : ""}. Videos and files go as a secure link in the text.
            </DialogDescription>
          </DialogHeader>
          <PatientMedications patientId={endpoint ? appt.id : appt.patient_id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" />
          <TemplatePicker disabled={busy} appt={appt} onInsert={(t) => setBody((b) => (b.trim() ? `${b.trimEnd()}\n${t}` : t).slice(0, 1000))} />
          <Textarea data-testid="patient-message-body" rows={4} maxLength={1000} value={body} disabled={busy}
            onChange={(e) => setBody(e.target.value)} placeholder="Type your message…" />
          <div className="flex items-center justify-between gap-3 -mt-2 min-h-[20px]">
            <SaveTemplate body={body} disabled={busy} />
            <div className="text-[11px] text-slate-400 shrink-0">{body.length}/1000</div>
          </div>
          <FileLibraryPicker selectedId={saved?.id} disabled={busy} onPick={(f) => { setSaved(f); if (f) { setFile(null); setUploaded(null); } }} />
          {file ? (
            <div data-testid="patient-message-file" className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-[#0B1F44] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{file.name}</div>
                <div className="text-xs text-slate-500">{mb(file.size)}</div>
                {busy && progress && stage !== "saving" && (
                  <>
                    <Progress data-testid="patient-message-progress" value={progress.pct} className="h-1.5 mt-2" />
                    <UploadStats p={progress} />
                  </>
                )}
                {busy && stage === "saving" && <div data-testid="patient-message-saving" className="text-[11px] text-slate-500 mt-1">Uploaded. Saving the file, this can take up to a minute for large videos…</div>}
              </div>
              {!busy && <button data-testid="patient-message-remove-file" onClick={() => { setFile(null); setUploaded(null); }} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>}
              </div>
              <label className="flex items-center gap-2 mt-3 text-xs text-slate-600">
                <Checkbox data-testid="patient-message-save-library" checked={saveLib} disabled={busy || !!uploaded} onCheckedChange={(v) => setSaveLib(!!v)} />
                Save to saved files for next time
              </label>
              {saveLib && (
                <Input data-testid="patient-message-library-name" value={libName} maxLength={80} disabled={busy || !!uploaded}
                  onChange={(e) => setLibName(e.target.value)} placeholder="Name, e.g. Aftercare guide" className="mt-2 h-8 text-sm" />
              )}
            </div>
          ) : saved ? null : (
            <button data-testid="patient-message-attach" onClick={() => inputRef.current?.click()} disabled={busy}
              className="w-full border-2 border-dashed border-slate-200 hover:border-[#0B1F44] rounded-lg p-4 text-sm text-slate-500 flex items-center justify-center gap-2 transition-colors">
              <Paperclip className="h-4 w-4" /> Attach a video, photo, PDF or document (up to 500 MB)
            </button>
          )}
          <input ref={inputRef} data-testid="patient-message-file-input" type="file" accept={ACCEPT} className="hidden"
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          {(file || saved || body.trim()) && (
            <MessagePreview endpoint={endpoint || `/appointments/${appt.id}/send-message`} body={body}
              fileId={saved?.id || uploaded?.file_id} filename={!saved && !uploaded ? file?.name : undefined} />
          )}
          <div className="flex justify-end gap-2">
            <Button data-testid="patient-message-cancel" variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button data-testid="patient-message-send" onClick={send} disabled={busy || (!body.trim() && !file && !saved) || (saveLib && !libName.trim() && !uploaded)}
              className="bg-[#0B1F44] hover:bg-[#13306a] text-white">
              <Send className="h-4 w-4 mr-1.5" />{busy ? (stage === "saving" ? "Saving video…" : file && !uploaded && (progress?.pct ?? 0) < 100 ? `Uploading ${progress?.pct ?? 0}%…` : "Sending…") : "Send text"}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};
